import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { CursorCandles } from "@/components/CursorCandles";
import { Crosshair } from "@/components/Crosshair";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { MentionFeed, TweetFeed } from "@/components/rank/TweetFeed";
import { fetchMentions, fetchUserTweets } from "@/lib/api";

export const Route = createFileRoute("/feed/$handle")({
  head: ({ params }) => {
    const h = params.handle;
    return {
      meta: [
        { title: `@${h} posts and mentions — Hoodrank feed` },
        {
          name: "description",
          content: `Indexed posts and mentions for @${h}, with engagement counts and verified-only filtering. Part of the Hoodrank reputation stack.`,
        },
        { property: "og:title", content: `@${h} feed — Hoodrank` },
        {
          property: "og:description",
          content: `Posts and mentions for @${h} with engagement metrics and filters.`,
        },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  component: FeedPage,
});

type Tab = "Posts" | "Mentions";

function FeedPage() {
  const { handle } = Route.useParams();
  const [tab, setTab] = useState<Tab>("Posts");
  const [visited, setVisited] = useState(() => new Set<Tab>(["Posts"]));

  useEffect(() => {
    setTab("Posts");
    setVisited(new Set<Tab>(["Posts"]));
  }, [handle]);

  useEffect(() => {
    setVisited((prev) => {
      if (prev.has(tab)) return prev;
      const next = new Set(prev);
      next.add(tab);
      return next;
    });
  }, [tab]);

  return (
    <>
      <CursorCandles />
      <Crosshair />
      <SiteHeader />

      <main data-candles className="grid-paper min-h-[calc(100vh-4rem)]">
        <div className="shell py-14 md:py-20">
          <div className="grid grid-cols-1 items-end gap-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(240px,0.8fr)] lg:gap-12">
            <div>
              <p className="label mb-6">Feed · Hoodrank</p>
              <h1 className="max-w-[16ch] !text-[clamp(34px,9vw,84px)] break-words">
                @{handle} <span className="hollow">feed</span>
              </h1>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Link
                  to="/profile/$handle"
                  params={{ handle }}
                  className="btn btn-dark w-full sm:w-auto"
                >
                  ← Back to profile
                </Link>
                <Link to="/app" className="btn btn-outline w-full sm:w-auto">
                  Score this handle
                </Link>
              </div>
            </div>

            <aside className="border border-ink bg-bg-panel p-4">
              <p className="label border-b border-line pb-2">Feed scope</p>
              <ul className="mt-3 space-y-2">
                <li className="mono text-[12px] text-ink-soft">
                  <span className="text-ink-muted">›</span> Indexed posts only
                </li>
                <li className="mono text-[12px] text-ink-soft">
                  <span className="text-ink-muted">›</span> Mentions with
                  engagement
                </li>
                <li className="mono text-[12px] text-ink-soft">
                  <span className="text-ink-muted">›</span> Not a full X archive
                </li>
              </ul>
              <p className="mono mt-4 border-t border-line pt-3 text-[11px] text-ink-muted">
                Switch tabs below · engagement bars are relative to this page
              </p>
            </aside>
          </div>

          <nav
            className="tab-strip mt-10 -mx-5 px-5 sm:mx-0 sm:flex-wrap sm:px-0"
            aria-label="Feed sections"
          >
            {(["Posts", "Mentions"] as Tab[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                aria-current={tab === t}
                className={`mono border px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] ${
                  tab === t
                    ? "border-ink bg-accent text-accent-ink"
                    : "border-line text-ink-soft hover:border-ink hover:text-ink"
                }`}
              >
                {t}
              </button>
            ))}
          </nav>

          <div className="mt-8">
            {visited.has("Posts") ? (
              <div hidden={tab !== "Posts"}>
                <TweetFeed
                  key={`${handle}-posts`}
                  load={(page) => fetchUserTweets(handle, page)}
                />
              </div>
            ) : null}
            {visited.has("Mentions") ? (
              <div hidden={tab !== "Mentions"}>
                <MentionFeed
                  key={`${handle}-mentions`}
                  load={(page, order) =>
                    fetchMentions(handle, page, 8, order)
                  }
                />
              </div>
            ) : null}
          </div>

          <p className="mono mt-6 text-[12px] text-ink-muted">
            Posts and mentions via the Hoodrank API.
          </p>
        </div>
      </main>

      <SiteFooter />
    </>
  );
}
